<script>
  import { useForm } from '@inertiajs/svelte'
  import Layout from '../../layouts/layout.svelte'
  import PageHeader from '../../components/page_header.svelte'
  import FormLayout from '../../components/form_layout.svelte'
  import FormSection from '../../components/form_section.svelte'
  import PlaceSearch from '../../components/place_search.svelte'
  import GoogleMap from '../../components/google_map.svelte'
  import TextField from '../../components/form/text_field.svelte'
  import TextareaField from '../../components/form/textarea_field.svelte'
  import SelectField from '../../components/form/select_field.svelte'
  import NumberField from '../../components/form/number_field.svelte'
  import DateField from '../../components/form/date_field.svelte'
  import SubmitButton from '../../components/form/submit_button.svelte'
  import ShrineImageField from '../../components/shrine_attachment_field.svelte'
  import { Button } from '../../components/ui'
  import { liveValidate } from '../../lib/live_validation.svelte.js'

  // Validation is the server's: each change is checked live by a Precognition request to the
  // real create/update action, and again on Save. Raw values go up; the server ignores blank
  // coordinates and an untouched image/logo, stores untouched blank text as nil, and replaces
  // the pause dates with this list.
  let { college, back_url: backUrl, options = {} } = $props()

  const isEdit = !!college.id
  const url = isEdit ? `/colleges/${college.id}` : '/colleges'

  const form = useForm({
    college: {
      area_id: college.area_id,
      name: college.name,
      onboarding_text: college.onboarding_text,
      address: college.address,
      phone: college.phone,
      google_id: college.google_id,
      latitude: college.latitude,
      longitude: college.longitude,
      pause_dates: college.pause_dates.map((pauseDate) => ({ ...pauseDate })),
      image: null,
      remove_image: false,
      logo: null,
      remove_logo: false
    },
    return_to: backUrl
  }).withPrecognition(isEdit ? 'patch' : 'post', url)
  liveValidate(form, 'college')

  const errorFor = (field) => $form.errors[`college.${field}`]

  let hours = $state(college.hours ?? [])

  const onPlaceSelect = (place) => {
    $form.college.address = place.address ?? $form.college.address
    $form.college.phone = place.phone || $form.college.phone
    // Persisting the picked google_id lets the server refresh Google metadata
    // (name/website/hours) on save via update_google_place — old CMS parity
    if (place.placeId) $form.college.google_id = place.placeId
    $form.college.latitude = place.lat
    $form.college.longitude = place.lng
    hours = place.hours ?? []
  }

  const addPauseDate = () => {
    $form.college.pause_dates = [...$form.college.pause_dates, { ...options.new_pause_date }]
  }

  const removePauseDate = (index) => {
    $form.college.pause_dates = $form.college.pause_dates.filter((_, i) => i !== index)
  }

  const submit = (event) => {
    event.preventDefault()
    if (isEdit) $form.patch(url)
    else $form.post(url)
  }
</script>

<Layout>
  <PageHeader title={isEdit ? 'Edit College' : 'New College'} subtitle="Schools within each college town" />

  <FormLayout onsubmit={submit}>
    <FormSection title="Details">
      <SelectField bind:value={$form.college.area_id} label="College Town" name="area_id" options={options.areas} error={errorFor('area_id')} required />
      <TextField bind:value={$form.college.name} label="Name" name="name" error={errorFor('name')} required />
      <TextareaField bind:value={$form.college.onboarding_text} label="Onboarding Text" name="onboarding_text" error={errorFor('onboarding_text')} />
    </FormSection>

    <FormSection title="Location">
      <fieldset class="fieldset">
        <legend class="fieldset-legend">Google Maps</legend>
        <PlaceSearch onselect={onPlaceSelect} />
        <div class="mt-3">
          <GoogleMap bind:lat={$form.college.latitude} bind:lng={$form.college.longitude} />
        </div>
      </fieldset>

      <div class="rounded-box border border-base-300 p-4 space-y-2">
        <h3 class="font-semibold text-center">Location Information</h3>
        <div class="grid grid-cols-2 gap-4 text-sm">
          <NumberField bind:value={$form.college.latitude} label="Latitude" name="latitude" step="0.0001" error={errorFor('latitude')} />
          <NumberField bind:value={$form.college.longitude} label="Longitude" name="longitude" step="0.0001" error={errorFor('longitude')} />
        </div>
        <p><span class="font-medium">Address:</span> {$form.college.address || 'No address yet. Search a place above.'}</p>
        {#if $form.college.phone}<p><span class="font-medium">Phone:</span> <a class="link" href={`tel:${$form.college.phone}`}>{$form.college.phone}</a></p>{/if}
        {#if college.google_maps_name}<p><span class="font-medium">Google Name:</span> {college.google_maps_name}</p>{/if}
        {#if hours.length > 0}
          <div>
            <span class="font-medium">Hours:</span>
            {#each hours as line (line)}<p class="text-xs">{line}</p>{/each}
          </div>
        {/if}
      </div>
    </FormSection>

    <FormSection title="Pause Dates">
      <fieldset class="fieldset">
        <div class="space-y-2">
          {#each $form.college.pause_dates as pauseDate, index (index)}
            <div class="flex items-center gap-2">
              <input type="text" class="input input-sm flex-1" placeholder="Description" bind:value={pauseDate.name} />
              <DateField bind:value={pauseDate.date} name={`pause_date_${index}`} />
              <Button variant="ghost" size="xs" class="text-error" onclick={() => removePauseDate(index)}>Remove</Button>
            </div>
          {/each}
        </div>
        {#if errorFor('pause_dates')}<p class="text-error text-sm mt-1">{errorFor('pause_dates')}</p>{/if}
        <Button variant="default" outline size="sm" class="mt-2" icon="add" onclick={addPauseDate}>Add Date</Button>
      </fieldset>
    </FormSection>

    <FormSection title="Media">
      <div class="grid grid-cols-2 gap-6">
        <ShrineImageField bind:value={$form.college.image} bind:remove={$form.college.remove_image} previewUrl={college.image_url} label="Image" />
        <ShrineImageField bind:value={$form.college.logo} bind:remove={$form.college.remove_logo} previewUrl={college.logo_url} label="Logo" />
      </div>
    </FormSection>

    {#if errorFor('base')}<p class="text-sm text-error">{errorFor('base')}</p>{/if}

    {#snippet footer()}
      <Button href={backUrl} variant="ghost" size="sm">Cancel</Button>
      <SubmitButton processing={$form.processing} full={false} />
    {/snippet}
  </FormLayout>
</Layout>
